import { type FC, Suspense } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslations } from 'next-intl';

import { DateRangeSelector } from '@features/filters';
import {
  AssignedUserMenu,
  MerchantDecisionsSelector,
  useUsers,
} from '@features/merchant';

import {
  initialMerchantSectionFilters,
  initialMerchantsFilters,
  type RequestForm,
} from '@entities/merchant';

import { AnalyticsEvent } from '@shared/analytics';
import { Flex, IconButton, Icons, Text } from '@shared/ui-kit';

import { isMerchantsFiltersActive } from '../../lib';

interface Props {
  isLoading: boolean;
}

const MerchantSection: FC<Props> = (props) => {
  const { isLoading = false } = props;

  const t = useTranslations();

  const { users, isLoading: isUsersLoading } = useUsers();

  const { control, reset } = useFormContext<RequestForm>();
  const formValues = useWatch({ control });
  const { filter } = formValues;

  const hasFilters = isMerchantsFiltersActive(initialMerchantsFilters, filter);

  const resetFilters = () => {
    reset({
      ...formValues,
      filter: {
        ...filter,
        ...initialMerchantSectionFilters,
      },
    });
  };

  return (
    <Flex
      direction="column"
      rowGap="4"
    >
      <Flex
        alignItems="center"
        columnGap="4"
      >
        <Text
          fontSize="sm"
          as="b"
        >
          {t('Merchant.title')}
        </Text>
        <IconButton
          data-testid="reset-filters"
          aria-label={t('Filters.resetButtonAriaLabel')}
          icon={
            <Icons.ClearFilters
              w="5"
              h="5"
            />
          }
          variant="ghost"
          size="xs"
          m="0"
          isDisabled={isLoading}
          visibility={hasFilters ? 'visible' : 'hidden'}
          transition="all .2s linear"
          onClick={resetFilters}
        />
      </Flex>
      <MerchantDecisionsSelector
        name="filter.decision"
        control={control}
      />
      <DateRangeSelector
        analyticsEvent={AnalyticsEvent.MerchantDecisionDateFilter}
        name="filter.decision_applied_at"
        label={t('Marketplace.Filters.DateRangeSelector.decision')}
        control={control}
      />
      <Suspense>
        <AssignedUserMenu
          defaultValue={undefined}
          name="filter.assigned_to"
          control={control}
          users={users}
          isUsersLoading={isUsersLoading}
        />
      </Suspense>
    </Flex>
  );
};

export default MerchantSection;
